'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SegmentedControl } from '@/components/ui/segmented-control'
import {
  formatOrderbookHeatmapSize,
  type HeatmapSizeFilter,
  isHeatmapSizeFilterActive,
  ORDERBOOK_HEATMAP_SIZE_PRESETS,
  parseOrderbookHeatmapSize,
} from '@/features/chart/lib/orderbook-heatmap'
import { cn } from '@/lib/utils'

/** One bound of the size filter: free text that takes shorthand ("500k", "1m"), committed on
 *  Enter or blur. */
function SizeBoundInput({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string
  value: number
  placeholder: string
  onChange: (value: number) => void
}) {
  const [draft, setDraft] = useState(() => formatOrderbookHeatmapSize(value))
  useEffect(() => setDraft(formatOrderbookHeatmapSize(value)), [value])

  const commit = () => {
    const next = parseOrderbookHeatmapSize(draft)
    if (next !== value) onChange(next)
    else setDraft(formatOrderbookHeatmapSize(value))
  }

  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-foreground/40 px-1 text-[10px] uppercase">{label}</span>
      <Input
        value={draft}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          // The menu's typeahead would otherwise take the keystroke.
          event.stopPropagation()
          if (event.key === 'Enter') commit()
        }}
        inputClassName="h-7 px-2 text-xs"
        className="h-7"
      />
    </label>
  )
}

/**
 * Order-book heatmap size filter, shared by both chart engines through chartOption: the range
 * as one Min – Max row, quick picks that set the floor (the common case: hide the small
 * levels), and a Clear that keeps its slot so nothing shifts when it becomes available.
 */
export function HeatmapSizeFilterContent({
  filter,
  onChange,
}: {
  filter: HeatmapSizeFilter
  onChange: (filter: HeatmapSizeFilter) => void
}) {
  const t = useTranslations('trade.chartSettings')
  const active = isHeatmapSizeFilterActive(filter)
  const presetValue = ORDERBOOK_HEATMAP_SIZE_PRESETS.find((preset) => preset === filter.min)

  return (
    <div className="flex w-64 flex-col gap-3 p-2">
      <div className="flex items-end gap-2">
        <SizeBoundInput
          label={t('heatmapMinSize')}
          value={filter.min}
          placeholder="0"
          onChange={(min) => onChange({ ...filter, min })}
        />
        <span className="text-foreground/30 pb-1.5 text-xs" aria-hidden="true">
          –
        </span>
        <SizeBoundInput
          label={t('heatmapMaxSize')}
          value={filter.max}
          placeholder="∞"
          onChange={(max) => onChange({ ...filter, max })}
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-foreground/40 px-1 text-[10px] uppercase">
          {t('heatmapMinPresets')}
        </span>
        <SegmentedControl
          size="xs"
          fill
          value={presetValue === undefined ? '' : String(presetValue)}
          onChange={(value) => onChange({ ...filter, min: Number(value) })}
          options={ORDERBOOK_HEATMAP_SIZE_PRESETS.map((preset) => ({
            value: String(preset),
            label: formatOrderbookHeatmapSize(preset),
          }))}
        />
      </div>
      <div className="flex items-start justify-between gap-2">
        <p className="text-foreground/40 px-1 text-[10px]">{t('heatmapFilterHint')}</p>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className={cn('shrink-0', !active && 'invisible')}
          aria-hidden={!active}
          tabIndex={active ? undefined : -1}
          onClick={() => onChange({ min: 0, max: 0 })}
        >
          {t('heatmapFilterClear')}
        </Button>
      </div>
    </div>
  )
}
